<script lang="ts" setup>
  import { LMarkerClusterGroup } from 'vue-leaflet-markercluster'

  // Config
  const appConfig = useAppConfig()

  // Emits
  const emit = defineEmits(['search-map', 'zoom-map'])

  // Router
  const router = useRouter()
  const route = useRoute()

  // Idioma
  const { locale } = useI18n()

  // Currency Store
  const currencyStore = useCurrencyStore()
  const { currency } = storeToRefs(currencyStore)

  // Ubicacion
  const selectedDestStore = useDestinationStore()
  const { selectedDestination, latitude, longitude } = storeToRefs(selectedDestStore)

  // Props
  const props = defineProps({
    boundHoteles: Array,
    lat: Number,
    lon: Number,
    allHotels: Object,
    hoverHotel: Number,
    strapiData: {
      type: Object,
      default: undefined
    },
    urlParams: String,
    pending: Boolean,
  })

  const mapRef = ref<MapRef | null>(null)
  const zoomMapa = ref<number>(12)

  const latitudesAndLongitudes = computed((): Array<[number, number]> => { // centrar mapa dependiendo de los markers de los hoteles
    return props.boundHoteles?.map((hotel: any) => {
      return [Number(hotel?.latitude), Number(hotel?.longitude)]
    }) || []
  })

  watch(() => props.pending, (newPending) => {
    if(!newPending) {
      if(latitudesAndLongitudes.value.length > 1) {
        boundMap(latitudesAndLongitudes.value)
      }else if(latitudesAndLongitudes.value.length === 1) {
        moverMapa({ lat: latitudesAndLongitudes.value[0][0], lng: latitudesAndLongitudes.value[0][1] })
      }
    }
  })

  const boundMap = (arrayLatitudes: Array<[number, number]>) => {
    if(arrayLatitudes.length === 0) return
    if(selectedDestination.value?.id === null) return
    mapRef.value?.leafletObject?.fitBounds(arrayLatitudes)
  }

  const visibleZona = ref(false)
  const isUserMoving = ref(false)
  
  const moverMapa = (center: { lat: number, lng: number }) => {
    centerLonLat.value = center
    if (isUserMoving.value) {
      visibleZona.value = true // mostrar botón de volver a la zona
    }
  }

  const centerLonLat = ref<{ lat: number, lng: number }>({ lat: props?.lat ?? 0, lng: props?.lon ?? 0 })

  const actualizarZoom = (zoom: number) => {
    zoomMapa.value = zoom
    // Solo marcar como movimiento de usuario si no estamos en pending
    if (!props.pending) {
      isUserMoving.value = true
    }
  }

  const deviceStore = useDeviceStore();
  const isMobile = deviceStore.isMobile;
  const isGoogle = ref(true)

  const isSearch = router.currentRoute.value.path.includes('search')

  // Favorites Store
  const favoritesStore = useFavoritesStore()
  const { isFavorite } = favoritesStore

  // Sincronizar favoritos iniciales cuando se cargan los hoteles
  watch(() => props.allHotels, (newHotels) => {
    if (newHotels) {
      favoritesStore.syncHotelFavorites(newHotels)
    }
  }, { immediate: true })

  const buscarEnZona = () => {
    emit('search-map', {
      center: { lat: centerLonLat.value.lat, lng: centerLonLat.value.lng },
      zoom: zoomMapa.value
    })
    visibleZona.value = false
  }

  watch(route, (newQuery, oldQuery) => {    
    if(!isSearch) return
    // Cuando cambia la ruta, actualizar el centro del mapa con zoom 12
    const newLat = Number(newQuery.query['data-latitude'] ?? props.lat ?? latitude.value ?? 0)
    const newLng = Number(newQuery.query['data-longitude'] ?? props.lon ?? longitude.value ?? 0)
        
    // Resetear el estado de movimiento de usuario
    isUserMoving.value = false
    visibleZona.value = false
    
    // Actualizar centro y zoom
    centerLonLat.value = { lat: newLat, lng: newLng }
    zoomMapa.value = 12
        
    // Forzar la actualización del mapa usando los métodos de Leaflet
    if (mapRef.value?.leafletObject) {
      const map = mapRef.value.leafletObject as any
      map.setView([newLat, newLng], 12)
    }
  }, { immediate: true, deep: true })

  // Watcher para las props de lat y lon
  watch([() => props.lat, () => props.lon], ([newLat, newLon]) => {
    if (newLat !== undefined && newLon !== undefined) {
      centerLonLat.value = { lat: newLat, lng: newLon }
      zoomMapa.value = 12
      isUserMoving.value = false
      visibleZona.value = false
      
      // Forzar la actualización del mapa usando los métodos de Leaflet
      if (mapRef.value?.leafletObject) {
        const map = mapRef.value.leafletObject as any
        map.setView([newLat, newLon], 12)
      }
    }
  })

  const today = ref(formatDateApi(new Date()))
  const dispo = ref(false)
  const showSkeleton = ref(false)
  const searchAvailabilty = async (hotel_id: number) => {
    showSkeleton.value = true
    
    const { data: dispoData, status }: any = await useFetch(`${useBaseUrl()}/hotel-days-availability`, {
      key: `dispo-${hotel_id}-${today.value}`,
      headers: { 
        "Content-Type": "application/json",
      },
      query:{
        date: today.value,
        hotel_id,
        channel_id: appConfig.channel_id,
        currency,
        months: 12
      },
      onRequestError: (error) => {
        logError(error)
        showSkeleton.value = false
      }
    })
    
    if (status.value === 'success') {
      dispo.value = !objIsEmpty(dispoData.value?.data?.pack_hour)
      showSkeleton.value = false
    }
  }
  
</script>
<template>

  <!-- Botón buscar en esta zona -->
  <transition
    enter-active-class="transition-opacity duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-300"
    leave-from-class="opacity-100" 
    leave-to-class="opacity-0"
  >
    <div v-if="(visibleZona && isSearch) || pending" class="absolute top-4 left-1/2 -translate-x-1/2 z-20">
      <UButton
        size="sm"
        color="neutral"
        variant="soft"
        class="shadow-md"
        @click="buscarEnZona"
        :icon="pending ? 'i-svg-spinners-3-dots-scale' : undefined"
        :label="pending ? undefined : $t('buttons.buscarZona')"
        :ui="{
          leadingIcon: 'size-6 mx-3 text-toned'
        }"
      />
    </div>
  </transition>
  <l-map
    class="w-full h-full rounded-3xl z-0"
    ref="mapRef"
    :max-zoom="18"
    :zoom="zoomMapa"
    :options="{zoomControl: false, attributionControl: false}"
    :center="[centerLonLat.lat, centerLonLat.lng]"
    @update:center="moverMapa"
    @update:zoom="actualizarZoom"
    @moveend="() => { if (!props.pending) isUserMoving = true }"
    :useGlobalLeaflet="true"
  >
    <l-tile-layer
      :url="isGoogle ? `http://{s}.google.com/vt/lyrs=m&hl=${locale}&x={x}&y={y}&z={z}` : `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png`"
      :minZoom= "3"
      :maxZoom= "18"
      :subdomains="['mt0', 'mt1', 'mt2', 'mt3']"
    />
    <l-marker v-if="!selectedDestination?.element_id && selectedDestination?.location" :lat-lng="[Number(selectedDestination.location.split(',')[0]), Number(selectedDestination.location.split(',')[1])]">
      <l-icon>
        <span class="relative flex size-3">
          <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#4285F4] opacity-50"></span>
          <span class="relative inline-flex size-3 rounded-full bg-[#4285F4] border border-white"></span>
        </span>
      </l-icon>
    </l-marker>
    <l-marker-cluster-group
      :showCoverageOnHover="false"
      :max-zoom="16"
      :disable-clustering-at-zoom="13"
      :max-cluster-radius="20"
      :spiderfy-on-max-zoom="false"
      :zoom-to-bounds-on-click="true"
      :remove-outside-visible-bounds="true"
    >
      <template v-for="hotel in allHotels">
        <l-marker 
          v-if="hotel?.id && !pending" 
          :lat-lng="[hotel?.latitude, hotel?.longitude]"
          @click="objIsEmpty(hotel?.pack_min_price) && !isSearch ? searchAvailabilty(hotel?.id) : null"
        >
          <l-icon v-if="isSearch">
            <div v-if="!objIsEmpty(hotel?.pack_min_price)" :id="hotel?.id" :class="`${hotel?.id === hoverHotel ? 'bg-primary' : 'bg-neutral-800' }`" class="hover:scale-105 duration-100 -ml-5 -mt-1 font-semibold p-1 px-2 rounded-full text-sm text-white w-fit text-nowrap flex items-center justify-center drop-shadow-md">
              <div class="flex items-center gap-1">
                <UIcon v-if="isFavorite(hotel?.id)" name="i-heroicons-heart-solid" class="text-xs text-error-500" />
                {{ primerPrecioLabel(hotel) }}
              </div>
            </div>
          </l-icon>
          <l-icon v-else>
            <div v-if="!objIsEmpty(hotel?.pack_min_price)" :id="hotel?.id" :class="`${hotel?.id === hoverHotel ? 'bg-primary' : 'bg-neutral-800' }`" class="hover:scale-105 duration-100 -ml-5 -mt-1 font-semibold p-1 px-2 rounded-full text-sm text-white w-fit text-nowrap flex items-center justify-center drop-shadow-md">
              <div class="flex items-center gap-1">
                <UIcon v-if="isFavorite(hotel?.id)" name="i-heroicons-heart-solid" class="text-xs text-error-500" />
                {{ primerPrecioLabel(hotel) }}
              </div>
            </div>
            <div v-else class="hover:scale-105 duration-100 bg-neutral-100 border border-neutral-300 drop-shadow-lg rounded-full p-1 px-3 -ml-4 text-black text-xs font-semibold w-fit text-nowrap flex items-center justify-center cursor-pointer">
              <UIcon name="i-heroicons-building-office-2" class="text-lg"  />
            </div>

          </l-icon>
          <l-popup class="w-60 sm:w-72" :options="{ closeButton: false }">
            <UCarousel
              :arrows="isMobile ? false : true"
              v-slot="{ item }"
              :items="hotel?.images"
              :class="`h-full`"
              :next="{ size: 'xs', variant: 'soft' }"
              :prev="{ size: 'xs', variant: 'soft' }"
              :ui="{
                item: 'basis-full h-full',
                container: 'h-full',
                viewport: 'aspect-16/9 w-full h-full rounded-2xl',
                next: 'sm:end-2',
                prev: 'sm:start-2'
              }"
            >
              <NuxtImg
                provider="byhours"
                format="webp"
                width="300"
                height="200"
                :src="generateImgUrl(String(item ?? ''))"
                @click="navigateTo(`/${locale}/${hotel.slug}-${hotel.id}.html?${urlParams ?? ''}`, { open: { target: '_blank' } })"
                :alt="`Imagen hotel`"
                class="w-full h-full object-cover cursor-pointer rounded-2xl"
                draggable="false"
                loading="lazy"
              />
            </UCarousel>
            <NuxtLink 
              :to = "`/${locale}/${hotel.slug}-${hotel.id}.html?${urlParams ?? ''}`"
              :target="isSearch ? '_blank' : '_self'"
              class="w-full flex flex-col p-4 justify-between bg-white shadow-[0px_0px_10px_0_rgba(0,0,0,0.05)] mt-2 rounded-2xl drop-shadow-md hover:drop-shadow-lg duration-300 transition-all"
              :aria-label="hotel?.name"
            >
              <div class="flex justify-between">
                <div class="flex flex-col">
                  <!-- Info hotel -->
                  <div class="flex text-neutral-900 mb-1">
                    <UIcon :key="index" v-for="index in splitEstrellas(hotel?.stars?.abbr).countEstrellas" name="i-heroicons-star-solid" class="text-xs text-neutral-800"/>
                    <span class="text-xs font-bold">{{ splitEstrellas(hotel?.stars?.abbr).rightString }}</span>
                  </div>
                  <p class="text-xs sm:text-base font-semibold my-0 text-neutral-800">{{ hotel?.name }}</p>
                </div>
              </div>
              <!-- Tabla de precios -->
              <div v-if="!objIsEmpty(hotel?.pack_min_price)" class="grid grid-rows-1 grid-flow-col grid-cols-3 gap-2 mt-1 sm:mt-2">
                <div v-if="hotel?.pack_min_price?.[3]?.price_label">
                  <div class="text-xs sm:text-xs text-neutral-500">3 {{ $t('labels.horas') }}</div>
                  <div class="text-xs sm:text-base text-neutral-800 font-bold">{{ hotel?.pack_min_price?.[3]?.price_label }}</div>
                </div>
                <div v-if="hotel?.pack_min_price?.[6]?.price_label">
                  <div class="text-xs sm:text-xs text-neutral-500">6 {{ $t('labels.horas') }}</div>
                  <div class="text-xs sm:text-base text-neutral-800 font-bold">{{ hotel?.pack_min_price?.[6]?.price_label }}</div>
                </div>
                <div v-if="hotel?.pack_min_price?.[24]?.price_label">
                  <div class="text-xs sm:text-xs text-neutral-500">24 {{ $t('labels.horas') }}</div>
                  <div class="text-xs sm:text-base text-neutral-800 font-bold">{{ hotel?.pack_min_price?.[24]?.price_label }}</div>
                </div>
              </div>
              <div v-else class="mt-2">
                <div v-if="showSkeleton" class="flex items-center space-x-2">
                  <div class="h-6 w-20 bg-gray-100 rounded-full animate-pulse"></div>
                </div>
                <template v-else>
                  <UBadge v-if="!dispo" color="neutral" variant="subtle" size="md">{{ $t('buttons.noDispo') }}</UBadge>
                  <UBadge v-else color="info" variant="subtle" size="md">{{ $t('buttons.dispoOtrosDias') }}</UBadge>
                </template>
              </div>
            </NuxtLink>
          </l-popup>
        </l-marker>
      </template>
    </l-marker-cluster-group>
  </l-map>
  
</template>

<style lang="css">

  .leaflet-div-icon {
    background-color: transparent;
    border: none;
  }

  .marker-cluster-medium,
  .marker-cluster-small {
    background-color: #1F2937;
    color: #fff;
  }

  .marker-cluster-medium div,
  .marker-cluster-small div {
    background-color: #1F2937;
    font-weight: bold;
  }

  .leaflet-popup-content-wrapper {
    background: transparent;
    box-shadow: none;
    padding: 0;
  }

  .leaflet-popup-content-wrapper .leaflet-popup-content {
    margin: 0;
  }

  .leaflet-popup-content-wrapper .leaflet-popup-content p {
    margin: 0;
  }

</style>